import { posthog } from "./posthog";

/**
 * Single source of truth for product-analytics event names.
 *
 * The PostHog dashboards (Analytics basics, conversion funnels) query the
 * literal strings on the right. Renaming a value here is a breaking change
 * for any saved insight or funnel that references it, update the dashboard
 * query at the same time.
 */
export const EVENTS = {
  USER_SIGNED_UP: "user_signed_up",
  USER_LOGGED_IN: "user_logged_in",
  USER_SIGNED_OUT: "user_signed_out",
  ONBOARDING_COMPLETED: "onboarding_completed",

  // Order funnel, prefixed `order_` so the breakdown view groups them
  // together and they don't collide with any future signup-side events
  // like `bakery_selected` for bakery-side onboarding.
  ORDER_BAKERY_SELECTED: "order_bakery_selected",
  ORDER_TREAT_SELECTED: "order_treat_selected",
  ORDER_CHECKOUT_INITIATED: "order_checkout_initiated",
  ORDER_PAYMENT_CONFIRMED: "order_payment_confirmed",
  ORDER_PAYMENT_FAILED: "order_payment_failed",

  // MS591/AUDIT-024: client-perceived latency for a single chat-concierge
  // turn (the `useInlineConversation` invoke() round-trip). This is the
  // UX number — network + full server turn + client render — and is the
  // counterpart to the server-persisted `chat_messages.duration_ms`.
  // Properties: client_perceived_ms (number), surface (chat /
  // dashboard-drawer / inline / floating), session_id, cold_start (bool).
  CHAT_TURN_LATENCY: "chat_turn_latency",

  // MS601 (Phase 6): the dashboard's one business-meaningful funnel
  // event — a buyer approved a recommendation from the ApprovalModal and
  // the celebration_recommendations row persisted (status="approved").
  // Fired only on a SUCCESSFUL send in Dashboard's handleSend, so the
  // count is "approvals that actually wrote to the DB," not "Send taps."
  // Payload is PII-free: a celebration id + line-item count only, never
  // recipient names / emails / gift details. The optional skipped event
  // is its negative-space counterpart (buyer declined the suggestion).
  DASHBOARD_GIFT_APPROVED: "dashboard_gift_approved",
  DASHBOARD_CELEBRATION_SKIPPED: "dashboard_celebration_skipped",
} as const;

export type EventName = (typeof EVENTS)[keyof typeof EVENTS];

type EventProps = Record<string, string | number | boolean | null | undefined>;

interface TrackOptions {
  /**
   * Bypass the default request-batching window and send the event
   * immediately. Use right before a `window.location.href` redirect
   * (e.g. Stripe Checkout), the default ~10s batch flush would lose
   * the event when the page unloads. Costs an extra HTTP request, so
   * leave off for normal in-app captures.
   */
  instant?: boolean;
}

/**
 * Capture a product-analytics event.
 *
 * Wrapped in try/catch because PostHog may not be initialised (key is
 * empty in the env) or the network request may fail, neither should
 * ever take down a flow. Same belt-and-braces stance as `sentry.ts`.
 */
export function track(
  event: EventName,
  props?: EventProps,
  options?: TrackOptions,
): void {
  try {
    posthog.capture(event, props, options?.instant ? { send_instantly: true } : undefined);
  } catch {
    // Swallow, analytics must never break a user flow.
  }
}

/**
 * Tie subsequent events to a known user. Idempotent, calling it again with
 * the same id is a no-op on PostHog's side, so it's safe to fire from the
 * auth state listener whenever a session is observed.
 *
 * Person-level traits go on `userProperties`; we keep these minimal because
 * `person_profiles: 'identified_only'` means anything we set here creates a
 * profile billable on the free tier.
 */
export function identifyUser(
  userId: string,
  userProperties?: EventProps,
): void {
  try {
    posthog.identify(userId, userProperties);
  } catch {
    // Same rationale as track().
  }
}

/**
 * Drop the current distinct id + cached identity. Called on signOut so
 * the next user on the same browser doesn't inherit the previous one's
 * identified profile.
 */
export function resetAnalytics(): void {
  try {
    posthog.reset();
  } catch {
    // Same rationale as track().
  }
}
